import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { AddAppointmentModal } from "../FullCalendar/AddAppointment";
import {
  fetchPractitionersByPractices,
  getAppointmentsByPracticeIds,
} from "../../api";
import { jwtDecode } from "jwt-decode";
import { EditAppointmentModal } from "./EditAppointments";
import {
  Col,
  Row,
  Container,
  Button,
  Offcanvas,
  Spinner,
  Badge,
} from "react-bootstrap";
import "./FullCalendar.css";

/* ---------------------- Shared Filters Content ---------------------- */
function FiltersContent({
  resourcesTitle,
  selectedResources,
  onToggleResource,
  canSelectMoreResources,
  selectedStatuses,
  onToggleStatus,
  statuses,
  onSelectAllResources,
  onClearResources,
  onSelectAllStatuses,
  onClearStatuses,
  userRole, // 👈 added
  userPermissions, // 👈 added
}) {
  const isPractitioner = userRole === "Practitioner";
  const isCalendarViewer = userPermissions === "CalendarViewer";
  const isRestricted = isPractitioner && isCalendarViewer;

  return (
    <>
      {/* Resources */}
      <div className="d-flex align-items-center justify-content-between">
        <h6 className="fw-bold text-primary mb-0">Resources</h6>
        {!isRestricted && (
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={onClearResources}
            >
              Clear
            </Button>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={onSelectAllResources}
            >
              Select all
            </Button>
          </div>
        )}
      </div>

      <div className="mt-2">
        {resourcesTitle?.length ? (
          resourcesTitle.map((resource) => {
            const checked = selectedResources.includes(resource.id);
            return (
              <div key={resource.id} className="form-check mb-1">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`resource-${resource.id}`}
                  checked={checked}
                  onChange={(e) =>
                    onToggleResource(resource.id, e.target.checked)
                  }
                  disabled={
                    isRestricted || (!checked && !canSelectMoreResources())
                  }
                />
                <label
                  htmlFor={`resource-${resource.id}`}
                  className={`form-check-label ${
                    isRestricted ? "text-muted" : "text-primary"
                  }`}
                >
                  {resource.title}
                </label>
              </div>
            );
          })
        ) : (
          <p className="text-muted">Loading resources…</p>
        )}
      </div>

      <hr className="my-3" />

      {/* Status */}
      <div className="d-flex align-items-center justify-content-between">
        <h6 className="fw-bold text-primary mb-0">Status</h6>
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={onClearStatuses}
          >
            Clear
          </Button>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={onSelectAllStatuses}
          >
            Select all
          </Button>
        </div>
      </div>

      <div className="mt-2">
        {statuses.map((status) => (
          <div
            key={status.label}
            className="form-check d-flex align-items-center mb-1"
          >
            <input
              type="checkbox"
              className="form-check-input me-2"
              id={`status-${status.label}`}
              checked={selectedStatuses.includes(status.label)}
              onChange={(e) => onToggleStatus(status.label, e.target.checked)}
            />
            <label
              className="form-check-label d-flex align-items-center text-primary"
              htmlFor={`status-${status.label}`}
            >
              <i className={`fa-light ${status.icon} ${status.color} me-2`} />
              {status.label}
            </label>
          </div>
        ))}
      </div>
    </>
  );
}

/* ---------------------------- Main Calendar ---------------------------- */
export default function Calendar() {
  const [events, setEvents] = useState([]);
  const [userId, setUserId] = useState();
  const [addModalShow, setAddModalShow] = useState(false);
  const [editModalShow, setEditModalShow] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [resourcesId, setResourcesId] = useState(null);
  const [resourcesName, setResourcesName] = useState(null);
  const [resourcesTitle, setResourcesTitle] = useState(null);
  const [practiceIds, setPracticeIds] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedResources, setSelectedResources] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [currentView, setCurrentView] = useState("resourceTimeGridDay");
  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 933);
  const [userRole, setUserRole] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [activeView, setActiveView] = useState("resourceTimeGridDay");
  const [userPermissions, setUserPermissions] = useState(null);
  const [loadedRange, setLoadedRange] = useState({ start: null, end: null });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pillMount, setPillMount] = useState(null);

  const calendarRef = useRef(null);

  const goToDayView = useCallback((dateLike) => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.changeView("resourceTimeGridDay", dateLike); // jump to day view on that date
  }, []);

  // Helper: fetch window = visible range + small buffer.
  // Day/4-day view = 2-week buffer either side; month view = 4-week buffer.
  // Tight buffers keep payloads small while still letting users page a few times
  // before a new fetch is triggered.
  const getFetchWindow = useCallback((viewType, viewStart, viewEnd) => {
    const bufferDays = viewType === "dayGridMonth" ? 28 : 14;
    const start = new Date(viewStart);
    start.setDate(start.getDate() - bufferDays);
    const end = new Date(viewEnd);
    end.setDate(end.getDate() + bufferDays);
    return {
      start: start.toISOString().split("T")[0],
      end: end.toISOString().split("T")[0],
    };
  }, []);

  const filteredEvents = useMemo(() => {
    return events.filter((e) =>
      selectedStatuses.includes(e.status ?? e.extendedProps?.status ?? "")
    );
  }, [events, selectedStatuses]);

  const filteredResources = useMemo(() => {
    if (!resourcesTitle) return [];

    const filtered = resourcesTitle.filter((r) =>
      selectedResources.includes(r.id)
    );

    if (currentView === "customFourDayView") {
      return filtered.slice(0, isMobile ? 1 : 3);
    }

    return filtered;
  }, [resourcesTitle, selectedResources, currentView, isMobile]);

  const renderEventContent = useCallback((arg) => {
    const { event, view } = arg;
    const ext = event.extendedProps || {};
    const isMonthView = view.type === "dayGridMonth";

    const isRecurring = !!ext.isRecurring;
    const hasIntakeForm = !!ext.intakeSessionTokenId;
    const isNotesDone = !!ext.isNotesDone;
    const isInvoiced = !!ext.isInvoiced;
    const isInvoiceEmailed = !!ext.isInvoiceEmailed;
    const isPaid = !!ext.isPaid;

    // Cover badge (I/M/P) — only for real appointments (patientId present)
    const isAppointment = !!ext.patientId;
    let coverBadge = null;
    if (isAppointment) {
      if (ext.patientIODFund) {
        coverBadge = { letter: "I", bg: "#ffc107", color: "#000", title: "IOD" };
      } else if (ext.patientMedicalAid) {
        coverBadge = { letter: "M", bg: "#0d6efd", color: "#fff", title: "Medical Aid" };
      } else {
        coverBadge = { letter: "P", bg: "#6c757d", color: "#fff", title: "Private" };
      }
    }

    const icons = [];

    if (isRecurring)
      icons.push({
        icon: "fa-solid fa-rotate",
        title: "Recurring appointment",
      });

    if (hasIntakeForm)
      icons.push({
        icon: "fa-light fa-file-circle-info",
        title: "Intake form sent",
      });

    if (isNotesDone)
      icons.push({
        icon: "fa-light fa-clipboard-check",
        title: "Notes completed",
      });

    if (isInvoiced)
      icons.push({
        icon: "fa-light fa-file-invoice-dollar",
        title: "Invoiced",
      });

    if (isInvoiceEmailed)
      icons.push({
        icon: "fa-light fa-envelope",
        title: "Invoice emailed",
      });

    if (isPaid)
      icons.push({
        icon: "fa-light fa-credit-card",
        title: "Paid",
      });

    const title = event.title || "";

    // Badge is rendered AFTER the title (matches EditAppointments.jsx style),
    // with a small left margin to keep it from butting up against the text.
    const coverBadgeHTML = (size, plain = false) =>
      coverBadge
        ? plain
          ? `<span title="${coverBadge.title}" style="display:inline-flex;align-items:center;justify-content:center;color:#fff;font-size:${size};font-weight:700;line-height:1;flex-shrink:0;margin-left:6px;">${coverBadge.letter}</span>`
          : `<span title="${coverBadge.title}" style="display:inline-flex;align-items:center;justify-content:center;background:${coverBadge.bg};color:${coverBadge.color};font-size:${size};font-weight:600;line-height:1;border-radius:3px;padding:1px 4px;flex-shrink:0;margin-left:6px;">${coverBadge.letter}</span>`
        : "";

    // Month view: compact with colored dot + icons aligned to end
    if (isMonthView) {
      const bgColor = event.backgroundColor || "#3788d8";
      const iconsHTML = icons
        .map(
          (ic) =>
            `<i class="${ic.icon}" title="${ic.title}" style="font-size:0.65rem;color:#0f3490;"></i>`
        )
        .join("");

      return {
        html: `
        <div class="fc-daygrid-event-content" style="display:flex;align-items:center;gap:4px;overflow:hidden;width:100%;">
          <span class="fc-daygrid-event-dot" style="border-color:${bgColor};flex-shrink:0;"></span>
          <span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:0.75rem;">${title}</span>
          ${coverBadgeHTML("0.6rem")}
          <span style="display:flex;gap:1px;flex-shrink:0;margin-left:auto;">${iconsHTML}</span>
        </div>
      `,
      };
    }

    // Day/resource views: scale title, icons, and badge to slot duration so
    // 15-min and 30-min bars don't overflow / look sloppy.
    const start = event.start;
    const end = event.end;
    let durationMinutes = 60;
    if (start && end) {
      durationMinutes = (end - start) / (1000 * 60);
    }

    let titleSize, iconSize, badgeSize, gap;
    if (durationMinutes <= 15) {
      titleSize = "0.6rem";
      iconSize = "0.5rem";
      badgeSize = "0.5rem";
      gap = "2px";
    } else if (durationMinutes <= 30) {
      titleSize = "0.72rem";
      iconSize = "0.7rem";
      badgeSize = "0.62rem";
      gap = "3px";
    } else {
      titleSize = ""; // fall through to CSS default (0.85rem)
      iconSize = "0.9rem";
      badgeSize = "0.7rem";
      gap = "";
    }

    const iconsHTML = icons
      .map(
        (ic) =>
          `<i class="${ic.icon} text-white" title="${ic.title}" style="font-size:${iconSize};"></i>`
      )
      .join("");

    const containerStyle = gap ? `style="gap:${gap};"` : "";
    const titleStyle = titleSize ? `style="font-size:${titleSize};"` : "";
    const tinyClass = durationMinutes <= 15 ? " event-tiny" : "";

    return {
      html: `
      <div class="fc-event-title-container${tinyClass}" ${containerStyle}>
        <span class="event-title" ${titleStyle}>${title}</span>
        ${coverBadgeHTML(badgeSize, true)}
        <span class="event-icons">${iconsHTML}</span>
      </div>
    `,
    };
  }, []);

  // Attach an `event-tiny-fc` class to the FullCalendar event wrapper for
  // short (≤15 min) events so we can style the FC-rendered shell directly.
  const eventClassNames = useCallback((arg) => {
    const start = arg.event.start;
    const end = arg.event.end;
    if (!start || !end) return [];
    const minutes = (end - start) / (1000 * 60);
    return minutes <= 15 ? ["event-tiny-fc"] : [];
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 933);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("user");
    if (!token) {
      console.error("No user token found.");
      setIsLoading(false);
      return;
    }

    const loadCalendarData = async () => {
      try {
        const decoded = jwtDecode(token);

        setUserId(decoded._id);
        setPracticeIds(decoded.practice || []);

        // Handle role as array or string
        const userRoles = Array.isArray(decoded.role)
          ? decoded.role
          : [decoded.role];
        const isPractitioner =
          userRoles.includes("Practitioner") && userRoles.length === 1;

        const roleToSet = isPractitioner ? "Practitioner" : "Admin";
        setUserRole(roleToSet);

        const userPermissions = Array.isArray(decoded.permissions)
          ? decoded.permissions
          : [decoded.permissions];
        const isCalViewer =
          userPermissions.includes("CalendarViewer") && userPermissions.length === 1;

        const permissionsToSet = isCalViewer ? "CalendarViewer" : null;
        setUserPermissions(permissionsToSet);

        const userName = `${decoded.firstName || ""} ${
          decoded.lastName || ""
        }`.trim();

        // Only fetch practitioners up front. Appointments are fetched lazily
        // on first datesSet for the visible window only.
        const practitionersResponse = await fetchPractitionersByPractices(
          decoded.practice
        );

        // if Practitioner role with CalendarViewer permission, limit to their own resource
        if (isPractitioner && isCalViewer) {
          const ownPractitioner = practitionersResponse.find(
            (p) =>
              p.title?.toLowerCase().includes(userName.toLowerCase()) ||
              p.id === decoded._id
          );

          if (ownPractitioner) {
            setResourcesTitle([ownPractitioner]);
            setSelectedResources([ownPractitioner.id]);
          } else {
            console.warn(
              "Practitioner not matched to resource list:",
              userName
            );
            setResourcesTitle([]);
            setSelectedResources([]);
          }
        } else {
          // normal access for admin roles or multiple roles
          setResourcesTitle(practitionersResponse);
          setSelectedResources(practitionersResponse.map((r) => r.id));
        }
      } catch (err) {
        console.error("Error loading calendar data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadCalendarData();
  }, []);

  const handleAddEvent = (newEvent) =>
    setEvents((prevEvents) => [...prevEvents, newEvent]);

  const dayClickAction = (info) => {
    if (activeView === "dayGridMonth") {
      // keep filters open (as requested)
      goToDayView(info.date);
      return;
    }

    // Resource views: open Add modal
    setSelectedDate(info.dateStr);
    setAddModalShow(true);

    if (info.resource) {
      setResourcesId(info.resource.id);
      setResourcesName(info.resource.title);
    }
  };

  const pad = (n) => String(n).padStart(2, "0");
  const toNaiveLocal = (v) => {
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v))
      return v;
    const d = v instanceof Date ? v : new Date(v);
    if (isNaN(d)) return "";
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const compactAddress = (loc = {}) => {
    const cityProv = [loc.city, loc.province].filter(Boolean).join(", ");
    return [loc.address1, loc.address2, cityProv, loc.postalCode]
      .filter(Boolean)
      .join(" | ");
  };

  const handleEventClick = useCallback(
    (clickInfo) => {
      const ev = clickInfo.event;
      const ext = ev.extendedProps || {};

      // Prefer the DB strings we copied onto extendedProps
      const startStr = ext.startRaw || toNaiveLocal(ev.start);
      const endStr = ext.endRaw || toNaiveLocal(ev.end);

      // Build location from object; fall back to flattened fields if needed
      const loc = ext.location || {
        name: ext.locationName || "",
        address1: ext.locationAddress || "",
        address2: ext.locationAddress2 || "",
        city: ext.locationCity || "",
        province: ext.locationProvince || "",
        postalCode: ext.locationPostalCode || "",
      };

      const newSelectedEvent = {
        id: ev._def?.publicId || ev.id || "",
        title: ev.title || "",
        status: ext.status || "",
        patientFirstName: ext.patientFirstName || "",
        patientLastName: ext.patientLastName || "",
        patientEmail: ext.patientEmail || "",
        patientPhone: ext.patientPhone || "",
        notes: ext.notes || "",
        start: startStr,
        end: endStr,
        resourceId: ext.resourceId || ev._def?.resourceIds?.[0] || "",
        resourceName:
          ext.resourceName ??
          (ev._def?.resourceIds
            ? resourcesTitle.find((r) => r.id === ev._def.resourceIds[0])?.title
            : "") ??
          "",
        practiceId: ext.practiceId || "",
        serviceId: ext.serviceId || "",
        patientId: ext.patientId || "",
        patientMedicalAid: ext.patientMedicalAid || "",
        patientIODFund: ext.patientIODFund || "",
        patientIODFundCategory: ext.patientIODFundCategory || "",

        // Location
        location: loc,
        locationName: loc.name || "",
        locationAddress: compactAddress(loc),
        isNotesDone: !!ext.isNotesDone,
        isInvoiced: !!ext.isInvoiced,
        isInvoiceEmailed: !!ext.isInvoiceEmailed,
        isPaid: !!ext.isPaid,
        isRecurring: !!ext.isRecurring,
        recurringID: ext.recurringID || "",
        intakeSessionTokenId: ext.intakeSessionTokenId || null,
        reminderLog: ext.reminderLog || [],
      };

      setSelectedEvent(newSelectedEvent);
    },
    [resourcesTitle]
  );

  useEffect(() => {
    if (selectedEvent && selectedEvent.resourceId !== "N/A") {
      setEditModalShow(true);
    }
  }, [selectedEvent]);

  // Fetch more appointments when navigating outside loaded range
  const fetchAppointmentsForRange = useCallback(
    async (start, end) => {
      if (isLoadingMore || !practiceIds.length) return;

      setIsLoadingMore(true);
      try {
        const newAppointments = await getAppointmentsByPracticeIds(
          practiceIds,
          start,
          end
        );

        if (newAppointments) {
          // Merge with existing events, avoiding duplicates by id
          setEvents((prev) => {
            const existingIds = new Set(prev.map((e) => e.id));
            const newEvents = newAppointments
              .filter((e) => !existingIds.has(e.id))
              .map((evt) => ({
                ...evt,
                extendedProps: {
                  ...evt,
                  patientEmail: evt.patientEmail,
                  patientPhone: evt.patientPhone,
                  patientMedicalAid: evt.patientMedicalAid || "",
                  patientIODFund: evt.patientIODFund || "",
                  isNotesDone: !!evt.isNotesDone,
                  isInvoiced: !!evt.isInvoiced,
                  isInvoiceEmailed: !!evt.isInvoiceEmailed,
                  isPaid: !!evt.isPaid,
                  isRecurring: !!evt.isRecurring,
                  recurringID: evt.recurringID || "",
                },
              }));
            return [...prev, ...newEvents];
          });

          // Expand the loaded range (handle initial null prev values)
          setLoadedRange((prev) => ({
            start: !prev.start || start < prev.start ? start : prev.start,
            end: !prev.end || end > prev.end ? end : prev.end,
          }));
        }
      } catch (err) {
        console.error("Error fetching more appointments:", err);
      } finally {
        setIsLoadingMore(false);
      }
    },
    [isLoadingMore, practiceIds]
  );

  const handleViewChange = useCallback(
    (info) => {
      const newView = info.view.type;
      setCurrentView(newView);

      if (newView === "customFourDayView") {
        if (isMobile && selectedResources.length > 1) {
          setSelectedResources(selectedResources.slice(0, 1));
        } else if (!isMobile && selectedResources.length > 3) {
          setSelectedResources(selectedResources.slice(0, 3));
        }
      }

      // Fetch appointments for the visible range (plus a small buffer) the
      // first time we see it, or when the user pages outside what we've loaded.
      const viewStart = info.view.activeStart?.toISOString().split("T")[0];
      const viewEnd = info.view.activeEnd?.toISOString().split("T")[0];

      if (!viewStart || !viewEnd) return;

      const needsFetch =
        !loadedRange.start ||
        !loadedRange.end ||
        viewStart < loadedRange.start ||
        viewEnd > loadedRange.end;

      if (needsFetch) {
        const win = getFetchWindow(
          newView,
          info.view.activeStart,
          info.view.activeEnd
        );
        fetchAppointmentsForRange(win.start, win.end);
      }
    },
    [
      isMobile,
      selectedResources,
      loadedRange,
      getFetchWindow,
      fetchAppointmentsForRange,
    ]
  );

  // Resource toggle with 4-day limits preserved
  const handleResourceSelection = (resourceId, isChecked) => {
    setSelectedResources((prevSelected) => {
      if (isChecked) {
        if (activeView === "dayGridMonth") {
          // month view = single select (replace)
          return [resourceId];
        }

        if (currentView === "customFourDayView") {
          if (isMobile && prevSelected.length >= 1) return prevSelected;
          if (!isMobile && prevSelected.length >= 3) return prevSelected;
        }

        return [...prevSelected, resourceId];
      } else {
        return prevSelected.filter((id) => id !== resourceId);
      }
    });
  };

  // Helper for Offcanvas to know if we can add more resources
  const canSelectMoreResources = () => {
    if (activeView === "dayGridMonth") return selectedResources.length < 1;

    if (currentView !== "customFourDayView") return true;
    const limit = isMobile ? 1 : 3;
    return selectedResources.length < limit;
  };

  useEffect(() => {
    if (!resourcesTitle?.length) return;

    // Don't auto-select all in month view (month is single-resource)
    if (activeView === "dayGridMonth") return;

    setSelectedResources(resourcesTitle.map((r) => r.id));
  }, [resourcesTitle, activeView]);

  const statuses = useMemo(
    () => [
      { label: "Pending", icon: "fa-circle-question", color: "text-pending" },
      {
        label: "New Patient",
        icon: "fa-triangle-exclamation",
        color: "text-danger",
      },
      { label: "Confirmed", icon: "fa-calendar-check", color: "text-success" },
      { label: "No Show", icon: "fa-ban", color: "text-danger" },
      { label: "Arrived", icon: "fa-location-dot", color: "text-secondary" },
      { label: "In Session", icon: "fa-spinner", color: "text-info" },
      { label: "Completed", icon: "fa-circle-check", color: "text-primary" },
      { label: "Scheduling", icon: "fa-clock", color: "text-muted" },
      { label: "Cancelled", icon: "fa-circle-xmark", color: "text-cancelled" },
    ],
    []
  );

  useEffect(() => {
    const defaultStatuses = statuses
      .filter((s) => s.label !== "Cancelled")
      .map((s) => s.label);
    setSelectedStatuses(defaultStatuses);
  }, [statuses]);

  const handleStatusSelection = (status, isChecked) => {
    setSelectedStatuses((prev) =>
      isChecked ? [...prev, status] : prev.filter((s) => s !== status)
    );
  };

  // Bulk actions
  const selectAllResources = () => {
    if (!resourcesTitle?.length) return;

    if (activeView === "dayGridMonth") {
      setSelectedResources([resourcesTitle[0].id]); // month = first only
      return;
    }

    const limit =
      currentView === "customFourDayView" ? (isMobile ? 1 : 3) : Infinity;

    setSelectedResources(resourcesTitle.slice(0, limit).map((r) => r.id));
  };

  const clearResources = () => setSelectedResources([]);

  const selectAllStatuses = () =>
    setSelectedStatuses(statuses.map((s) => s.label));
  const clearStatuses = () => setSelectedStatuses([]);

  // Memoize filtered resources for performance

  useEffect(() => {
    if (activeView !== "dayGridMonth") return;

    // pick the first selected resource (or fall back to first available)
    setSelectedResources((prev) => {
      const first = prev?.[0] || resourcesTitle?.[0]?.id || null;

      return first ? [first] : [];
    });
  }, [activeView, resourcesTitle]);

  const selectedResourceId = selectedResources?.[0] || null;

  // Get the selected therapist name for month view header
  const selectedTherapistName = useMemo(() => {
    if (!selectedResourceId || !resourcesTitle?.length) return "";
    const resource = resourcesTitle.find((r) => r.id === selectedResourceId);
    return resource?.title || "";
  }, [selectedResourceId, resourcesTitle]);

  // Mount/unmount the resource quick-filter row directly after the FC toolbar
  // (above the calendar grid) — only in month view. We use a portal so the
  // pills can live inside FC's DOM without us forking FC's rendering.
  useEffect(() => {
    const calendarEl = calendarRef.current?.elRef?.current;
    if (!calendarEl) {
      setPillMount(null);
      return;
    }

    const isRestricted =
      userRole === "Practitioner" && userPermissions === "CalendarViewer";

    if (activeView !== "dayGridMonth" || isRestricted) {
      const existing = calendarEl.querySelector(".month-pills-mount");
      if (existing) existing.remove();
      setPillMount(null);
      return;
    }

    const toolbar = calendarEl.querySelector(".fc-header-toolbar");
    if (!toolbar) return;

    let mount = calendarEl.querySelector(".month-pills-mount");
    if (!mount) {
      mount = document.createElement("div");
      mount.className = "month-pills-mount";
      toolbar.insertAdjacentElement("afterend", mount);
    }
    setPillMount(mount);
  }, [activeView, userRole, userPermissions, isLoading]);

  // Strip any leftover therapist subtitle from earlier renders (the quick-filter
  // pills now show which resource is active, so the subtitle is redundant).
  useEffect(() => {
    const calendarEl = calendarRef.current?.elRef?.current;
    if (!calendarEl) return;
    const existingSubtitle = calendarEl.querySelector(".therapist-subtitle");
    if (existingSubtitle) existingSubtitle.remove();
  }, [activeView, selectedTherapistName]);

  const monthEvents = useMemo(() => {
    if (!selectedResourceId) return [];

    // keep your status filtering AND add resource filtering
    return filteredEvents.filter((e) => {
      const rid = e.resourceId ?? e.extendedProps?.resourceId;
      return rid === selectedResourceId;
    });
  }, [filteredEvents, selectedResourceId]);

  return (
    <div className="pt-5 mt-5" style={{ minHeight: "100vh" }}>
      {isLoading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "50vh" }}
        >
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading calendar...</span>
          </Spinner>
        </div>
      ) : (
        <div className="ms-1">
          <Row>
            {/* No sidebar anymore – filter lives in Offcanvas */}

            {/* Calendar */}
            <Col xs={12}>
              <FullCalendar
                className="calendar-container"
                ref={calendarRef}
                height={isMobile ? "75vh" : "85vh"}
                schedulerLicenseKey="0304135436-fcs-1780517653"
                plugins={[
                  dayGridPlugin,
                  resourceTimeGridPlugin,
                  interactionPlugin,
                ]}
                dateClick={dayClickAction}
                nowIndicator={true}
                views={{
                  customFourDayView: {
                    type: "resourceTimeGrid",
                    duration: { days: 4 },
                    buttonText: "4 DAYS",
                  },
                  day: {
                    titleFormat: {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      weekday: "short",
                    },
                  },
                }}
                buttonText={{
                  today: "TODAY",
                  month: "MONTH",
                  week: "WEEK",
                  day: "DAY",
                  resourceTimeGridDay: isMobile ? "DAY" : "DAY VIEW",
                  customFourDayView: isMobile ? "4D" : "4 DAYS",
                  timeGridMonth: isMobile ? "MONTH" : "MONTH VIEW",
                }}
                slotMinTime={"06:00:00"}
                slotMaxTime={"19:00:00"}
                initialView="resourceTimeGridDay"
                datesSet={(info) => {
                  setActiveView(info.view.type);
                  handleViewChange(info);
                }}
                eventClick={handleEventClick}
                titleFormat={
                  isMobile
                    ? { year: "2-digit", month: "short", day: "numeric" }
                    : { year: "numeric", month: "long", day: "numeric" }
                }
                headerToolbar={
                  isMobile
                    ? { left: "filterToggle", center: "title", right: "today" }
                    : {
                        left: "filterToggle,prev,next today",
                        center: "title",
                        right:
                          "dayGridMonth,resourceTimeGridDay,customFourDayView",
                      }
                }
                footerToolbar={
                  isMobile
                    ? {
                        left: "prev,next",
                        center: "",
                        right:
                          "dayGridMonth,resourceTimeGridDay,customFourDayView",
                      }
                    : false
                }
                customButtons={{
                  filterToggle: {
                    text: showFilters ? "HIDE" : "FILTER",
                    className: "btn btn-sm btn-outline-primary me-2 rounded-0",
                    style: { fontSize: "0.7rem", fontWeight: "600" },
                    click: () => setShowFilters((v) => !v),
                  },
                }}
                datesAboveResources={true}
                resources={filteredResources}
                events={
                  activeView === "dayGridMonth" ? monthEvents : filteredEvents
                }
                dayMaxEvents={activeView === "dayGridMonth" ? 3 : undefined}
                displayEventTime={activeView === "dayGridMonth" ? false : true}
                eventContent={renderEventContent}
                eventClassNames={eventClassNames}
                editable={false}
              />

              {/* Edit Modal */}
              <EditAppointmentModal
                event={selectedEvent}
                show={editModalShow}
                resources={selectedResources}
                practiceIds={practiceIds}
                setEvents={setEvents}
                onHide={() => {
                  setEditModalShow(false);
                  setSelectedEvent(null);
                }}
              />

              {/* Add Modal */}
              <AddAppointmentModal
                show={addModalShow}
                onHide={() => setAddModalShow(false)}
                selectedDate={selectedDate}
                resourcesId={resourcesId}
                resourcesName={resourcesName}
                onSave={handleAddEvent}
                events={events}
                practiceIds={practiceIds}
                setEvents={setEvents}
              />
            </Col>
          </Row>
        </div>
      )}

      {/* Month view: resource quick-filter badges, portaled below the FC toolbar */}
      {pillMount &&
        activeView === "dayGridMonth" &&
        resourcesTitle?.length > 0 &&
        createPortal(
          <div
            className="month-resource-quickfilter d-flex flex-nowrap gap-2 px-1 pb-2 pt-0 mt-0"
            style={{ overflowX: "auto" }}
          >
            {resourcesTitle.map((r) => {
              const isSelected = selectedResourceId === r.id;
              return (
                <Badge
                  key={r.id}
                  bg={isSelected ? "secondary" : "light"}
                  text={isSelected ? undefined : "secondary"}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedResources([r.id])}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelectedResources([r.id]);
                    }
                  }}
                  className="flex-shrink-0 border border-secondary"
                  style={{
                    cursor: "pointer",
                    fontSize: "0.7rem",
                    padding: "0.375em 0.75em",
                    fontWeight: 500,
                  }}
                >
                  {r.title}
                </Badge>
              );
            })}
          </div>,
          pillMount
        )}

      {/* Slide-out Filters (right side, like receipts) */}
      {!isLoading && (
        <Offcanvas
          placement="start"
          show={showFilters}
          onHide={() => setShowFilters(false)}
          scroll
          backdrop
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title className="text-primary">Filter</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Container className="p-0">
              <FiltersContent
                resourcesTitle={resourcesTitle}
                selectedResources={selectedResources}
                onToggleResource={handleResourceSelection}
                canSelectMoreResources={canSelectMoreResources}
                selectedStatuses={selectedStatuses}
                onToggleStatus={handleStatusSelection}
                statuses={statuses}
                onSelectAllResources={selectAllResources}
                onClearResources={clearResources}
                onSelectAllStatuses={selectAllStatuses}
                onClearStatuses={clearStatuses}
                userRole={userRole} // 👈 pass role down
                userPermissions={userPermissions} // 👈 pass permissions down
              />

              <div className="d-flex justify-content-end mt-3 gap-2">
                <Button
                  variant="outline-secondary"
                  className="rounded-0"
                  onClick={() => setShowFilters(false)}
                >
                  Close
                </Button>
                <Button
                  variant="primary"
                  className="rounded-0"
                  onClick={() => setShowFilters(false)}
                >
                  Apply
                </Button>
              </div>
            </Container>
          </Offcanvas.Body>
        </Offcanvas>
      )}
    </div>
  );
}
